import { useIonRouter } from '@ionic/react';
import { useQueryClient } from '@tanstack/react-query';
import {
  type GooglePlaceDto,
  LocationsApi,
} from '@up2stay/up2promo-api-client';
import { Button } from '@up2stay/up2promo-ui';
import { useState } from 'react';
import { toast } from 'sonner';

import { AppIonPage } from '@/core/components/app-ion-page';
import { AppPageContent } from '@/core/components/app-page-content';
import { AppPageLayout } from '@/core/components/app-page-layout';
import { LocationAddressEditor } from '@/core/components/locations/location-address-editor';
import {
  hasAddressContent,
  toAddressDetailsDto,
} from '@/core/components/locations/location-create-payload';
import {
  type LocationAddressDetails,
  LocationDetailsForm,
} from '@/core/components/locations/location-details-form';
import { LocationReviewSummary } from '@/core/components/locations/location-review-summary';
import { WizardShell } from '@/core/components/onboarding/wizard-shell';
import { PageHeader } from '@/core/components/page-header';
import {
  SettingsGroup,
  SettingsGroupFooter,
  SettingsGroupRow,
  SettingsGroupRows,
} from '@/core/components/settings/settings-group';
import { useIsMobile } from '@/core/hooks/use-mobile';
import {
  goBackOrFallback,
  useIonBackRouter,
} from '@/core/lib/ion-back-navigation';
import { locationsQueryKeys } from '@/core/query-keys/locations-query-keys';
import { useDictionary } from '@/core/providers/dictionary-provider';
import { shellPaths } from '@/core/router/app-paths';
import { useCurrentWorkspace } from '@/core/store/workspace.store';

enum WizardStep {
  DETAILS = 1,
  REVIEW = 2,
}

const TOTAL_STEPS = 2;

function CreateLocationBreadcrumb({
  domainLabel,
  locationsLabel,
}: {
  domainLabel: string;
  locationsLabel: string;
}) {
  return (
    <>
      {domainLabel}
      <span className="mx-1.5 text-muted-foreground/70">/</span>
      {locationsLabel}
    </>
  );
}

export function CreateLocationPage() {
  const dict = useDictionary();
  const isMobile = useIsMobile();
  const ionRouter = useIonRouter();
  const backRouter = useIonBackRouter();
  const goBack = (fallback: string) => goBackOrFallback(backRouter, fallback);
  const replaceWith = (path: string) => {
    ionRouter.push(path, 'forward', 'replace');
  };
  const queryClient = useQueryClient();
  const currentWorkspace = useCurrentWorkspace();
  const w = dict.locations?.create?.wizard;

  const [currentStep, setCurrentStep] = useState<WizardStep>(
    WizardStep.DETAILS,
  );
  const [direction, setDirection] = useState(1);
  const [name, setName] = useState('');
  const [latitude, setLatitude] = useState<number | undefined>(undefined);
  const [longitude, setLongitude] = useState<number | undefined>(undefined);
  const [addressDetails, setAddressDetails] =
    useState<LocationAddressDetails | null>(null);
  const [googlePlaceId, setGooglePlaceId] = useState<string | undefined>(
    undefined,
  );
  const [isCreating, setIsCreating] = useState(false);
  const [nameError, setNameError] = useState<string | null>(null);

  const stepLabels = [w?.steps?.details, w?.steps?.review].filter(
    (label): label is string => Boolean(label),
  );

  const canContinueFromDetails = name.trim().length > 0;

  const breadcrumb = (
    <CreateLocationBreadcrumb
      domainLabel={dict.nav.navDomainWorkspace}
      locationsLabel={dict.nav.locations}
    />
  );

  const handlePlaceSelect = (place: GooglePlaceDto) => {
    const placeId = place.id?.trim();
    setGooglePlaceId(placeId || undefined);
    if (!name.trim() && place.name) {
      setName(place.name);
      setNameError(null);
    }
  };

  const goToStep = (next: WizardStep) => {
    setDirection(next > currentStep ? 1 : -1);
    setCurrentStep(next);
  };

  const handleNext = () => {
    if (currentStep === WizardStep.DETAILS) {
      if (!name.trim()) {
        const msg = w?.errors?.nameRequired;
        setNameError(msg);
        toast.error(msg);
        return;
      }
      setNameError(null);
      goToStep(WizardStep.REVIEW);
      return;
    }
    void handleCreateLocation();
  };

  const handleBack = () => {
    if (currentStep > WizardStep.DETAILS) {
      goToStep((currentStep - 1) as WizardStep);
    }
  };

  const handleCreateLocation = async () => {
    if (!currentWorkspace) {
      toast.error(w?.errors?.noWorkspace);
      return;
    }
    if (!name.trim()) {
      toast.error(w?.errors?.nameRequired);
      return;
    }

    setIsCreating(true);
    try {
      const response = await LocationsApi.create({
        path: { workspaceId: currentWorkspace.id },
        body: {
          name: name.trim(),
          coordinates:
            latitude !== undefined && longitude !== undefined
              ? { latitude, longitude }
              : null,
          addressDetails: hasAddressContent(addressDetails)
            ? toAddressDetailsDto(addressDetails!)
            : null,
          ...(googlePlaceId
            ? { reviewExternalIds: { google: googlePlaceId } }
            : {}),
        },
      });

      if (response.data?.id) {
        await queryClient.invalidateQueries({
          queryKey: locationsQueryKeys.root,
        });
        toast.success(w?.success?.title);
        replaceWith(shellPaths.moreWorkspaceLocationDetail(response.data.id));
      }
    } catch (error) {
      console.error('Failed to create location:', error);
      const message =
        (error as { response?: { data?: { message?: string } } })?.response
          ?.data?.message ?? (error instanceof Error ? error.message : null);
      toast.error(message ?? w?.errors?.createFailed);
    } finally {
      setIsCreating(false);
    }
  };

  const handleBackToLocations = () => goBack(shellPaths.moreWorkspaceLocations);

  const addressLabels = {
    searchLabel: w?.detailsStep?.searchLabel,
    searchPlaceholder:
      w?.detailsStep?.searchPlaceholder ?? w?.searchStep?.placeholder,
    searchHint: w?.detailsStep?.searchHint,
    addressLabel: w?.detailsStep?.addressLabel,
    selectedEmptyLabel: w?.detailsStep?.selectedEmptyLabel,
    addressEditHint: w?.detailsStep?.addressEditHint,
    mapPreviewLabel: w?.reviewStep?.mapPreview,
    noCoordinatesLabel: w?.reviewStep?.noCoordinates,
    mapUnavailableLabel: w?.reviewStep?.mapUnavailable,
    dragHintLabel: w?.detailsStep?.dragHint,
  };

  const nextLabel =
    currentStep === WizardStep.REVIEW
      ? isCreating
        ? w?.creating
        : w?.createLocation
      : w?.continue;

  const nextDisabled =
    currentStep === WizardStep.DETAILS ? !canContinueFromDetails : isCreating;

  const stepTitleAlign = isMobile ? 'text-center' : 'text-start';

  return (
    <AppIonPage>
      <AppPageContent scrollY={false}>
        <div className="relative flex h-full min-h-0 flex-col">
          <AppPageLayout
            withGap={false}
            className="flex min-h-0 flex-1 flex-col"
          >
            <PageHeader
              breadcrumb={breadcrumb}
              title={w?.title}
              onBack={
                currentStep > WizardStep.DETAILS
                  ? handleBack
                  : handleBackToLocations
              }
              actions={
                <Button
                  type="button"
                  variant="ghost"
                  size="sm"
                  onClick={handleBackToLocations}
                  className="text-muted-foreground"
                >
                  {w?.cancel}
                </Button>
              }
            />
            <WizardShell
              currentStep={currentStep}
              totalSteps={TOTAL_STEPS}
              stepLabels={
                stepLabels.length === TOTAL_STEPS ? stepLabels : undefined
              }
              direction={direction}
              stepKey={currentStep}
              onBack={currentStep > WizardStep.DETAILS ? handleBack : undefined}
              onNext={handleNext}
              nextLabel={nextLabel}
              nextDisabled={nextDisabled}
              isLoading={isCreating}
              showBack={false}
              variant="shell"
              contentAlignment="start"
            >
              {currentStep === WizardStep.DETAILS ? (
                <div className="mx-auto flex w-full max-w-lg flex-col gap-5">
                  <div className={`flex flex-col gap-1 ${stepTitleAlign}`}>
                    <h2 className="text-balance text-[22px] font-bold leading-tight text-foreground sm:text-[24px]">
                      {w?.detailsStep?.title}
                    </h2>
                  </div>
                  <div className="flex flex-col gap-2">
                    <SettingsGroup>
                      <SettingsGroupRows>
                        <SettingsGroupRow>
                          <LocationDetailsForm
                            name={name}
                            onNameChange={(value) => {
                              setName(value);
                              if (value.trim() && nameError) setNameError(null);
                            }}
                            nameError={nameError}
                          />
                        </SettingsGroupRow>
                        <SettingsGroupRow separated>
                          <LocationAddressEditor
                            addressDetails={addressDetails}
                            onAddressDetailsChange={setAddressDetails}
                            latitude={latitude}
                            longitude={longitude}
                            onCoordinatesChange={(lat, lng) => {
                              setLatitude(lat);
                              setLongitude(lng);
                            }}
                            onPlaceSelect={handlePlaceSelect}
                            name={name}
                            disabled={isCreating}
                            labels={addressLabels}
                          />
                        </SettingsGroupRow>
                      </SettingsGroupRows>
                    </SettingsGroup>
                    {w?.detailsStep?.description ? (
                      <SettingsGroupFooter>
                        {w.detailsStep.description}
                      </SettingsGroupFooter>
                    ) : null}
                  </div>
                </div>
              ) : null}

              {currentStep === WizardStep.REVIEW ? (
                <div className="mx-auto flex w-full max-w-lg flex-col gap-5">
                  <div className={`flex flex-col gap-1 ${stepTitleAlign}`}>
                    <h2 className="text-balance text-[22px] font-bold leading-tight text-foreground sm:text-[24px]">
                      {w?.reviewStep?.title}
                    </h2>
                  </div>
                  <LocationReviewSummary
                    name={name}
                    address={addressDetails?.formatted ?? ''}
                    latitude={latitude}
                    longitude={longitude}
                    googlePlaceId={googlePlaceId}
                    addressDetails={addressDetails}
                    isManual={!googlePlaceId}
                  />
                  {w?.reviewStep?.description ? (
                    <SettingsGroupFooter>
                      {w.reviewStep.description}
                    </SettingsGroupFooter>
                  ) : null}
                </div>
              ) : null}
            </WizardShell>
          </AppPageLayout>
        </div>
      </AppPageContent>
    </AppIonPage>
  );
}
